import React, { useEffect, useState } from "react";
import { Plug, Loader2, AlertCircle, ExternalLink, Radio, CheckCircle2, X as XIcon, Activity, RefreshCw } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  publish: "Social publishing",
  distribution: "Podcast distribution",
  streaming: "Live streaming",
  automation: "Radio automation",
};

// ── Connection health strip — pings live connections, alerts on drops ──
function ConnectionHealthCard() {
  const [rows, setRows] = useState(null);
  const [checking, setChecking] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/connection-health");
      setRows(data.integrations || []);
    } catch { setRows([]); }
  };
  useEffect(() => { load(); }, []);

  const recheck = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/connection-health/check");
      setRows(data.integrations || []);
      const down = (data.integrations || []).filter((r) => r.status !== "ok");
      if (down.length === 0) toast.success("All connections healthy");
      else toast.warning(`${down.length} connection${down.length === 1 ? "" : "s"} need attention`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Health check failed");
    } finally { setChecking(false); }
  };

  const ago = (iso) => {
    if (!iso) return "";
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    return `${Math.floor(m / 60)}h ago`;
  };

  if (rows === null) return null;

  return (
    <div className="mb-8 rounded-xl border border-line bg-bg-surface p-5" data-testid="connection-health-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-brand-amber" />
          <span className="font-display font-bold text-sm">Connection health</span>
          <span className="font-mono text-[10px] text-ink-muted">auto-checked every 30 min · bell alerts on drops</span>
        </div>
        <button onClick={recheck} disabled={checking} data-testid="health-recheck-btn"
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] text-ink-secondary hover:text-ink-primary disabled:opacity-50 transition">
          <RefreshCw className={`h-3 w-3 ${checking ? "animate-spin" : ""}`} />
          {checking ? "Checking…" : "Re-check now"}
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-ink-muted" data-testid="health-empty">No live connections to monitor yet — connect an integration below.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {rows.map((r) => (
            <div key={r.integration}
              className={`rounded-lg border px-3 py-2.5 flex items-start gap-2.5 ${r.status === "ok" ? "border-line bg-bg-elevated/60" : "border-rose-500/40 bg-rose-500/5"}`}
              data-testid={`health-${r.integration}`}>
              <span className={`mt-1 h-2 w-2 rounded-full shrink-0 ${r.status === "ok" ? "bg-emerald-400" : "bg-rose-400"}`}
                style={{ boxShadow: r.status === "ok" ? "0 0 6px rgba(52,211,153,0.7)" : "0 0 6px rgba(251,113,133,0.7)" }} />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-ink-primary">{r.label}</div>
                <div className={`text-[10px] truncate ${r.status === "ok" ? "text-ink-muted" : "text-rose-300"}`} title={r.detail}>
                  {r.detail}
                </div>
                <div className="text-[9px] font-mono text-ink-muted mt-0.5">checked {ago(r.checked_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const CATEGORY_ORDER = ["automation", "publish", "distribution", "streaming"];

const CONFIG_FIELDS = {
  meta_facebook: ["app_id", "page_access_token"],
  meta_instagram: ["ig_business_account_id"],
  x_twitter: ["api_key", "api_secret", "bearer_token"],
  tiktok: ["client_key", "client_secret"],
  youtube: ["oauth_client_id", "oauth_client_secret"],
  linkedin: ["client_id", "client_secret"],
  whatsapp_business: ["phone_number_id", "access_token"],
  spotify: ["client_id", "client_secret"],
  apple_podcasts: ["rss_feed_url"],
  shoutcast: ["stream_url", "admin_password"],
  icecast: ["stream_url", "mount_point"],
  sam_broadcaster: ["stream_url"],
  broadcast_radio_uk: ["api_url", "station_id", "api_key"],
};

// Per-platform help copy shown in the config modal
const CONFIG_HELP = {
  broadcast_radio_uk: {
    label: "broadcastradio.com",
    url: "https://www.broadcastradio.com/myriad-playout",
    note: "Find your Myriad XAPI URL under Myriad Anywhere → Settings → API. The URL usually looks like https://yourstation.broadcastradio.com/xapi.",
  },
  meta_instagram: {
    label: "developers.facebook.com",
    url: "https://developers.facebook.com/docs/instagram-platform/content-publishing",
    note: "Leave the field empty — we auto-discover the Instagram Business account linked to your connected Facebook Page and reuse its Page token. Only fill it in to override the auto-discovery. Requires Facebook (Meta) to be connected first.",
  },
};

export default function Integrations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [config, setConfig] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testPosting, setTestPosting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/integrations");
      setItems(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (it) => {
    setEditing(it);
    setConfig({});
    setTestResult(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/integrations/${editing.platform}/connect`, { config });
      toast.success(res.data.message || "Saved");
      setEditing(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || (e?.response ? "Save failed" : "Network error — the server may be restarting. Try again in a few seconds."), { duration: 6000 });
    } finally { setSaving(false); }
  };

  const testBroadcastRadio = async () => {
    if (!config.api_url || !config.api_key) {
      toast.error("Fill in API URL and API key first");
      return;
    }
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post("/integrations/broadcast_radio_uk/test", {
        api_url: config.api_url, station_id: config.station_id || "", api_key: config.api_key,
      });
      setTestResult({ ok: true, ...data });
      toast.success(`Myriad reachable${data.myriad_version ? ` · v${data.myriad_version}` : ""}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Test failed";
      setTestResult({ ok: false, error: detail });
      toast.error(detail, { duration: 8000 });
    } finally { setTesting(false); }
  };

  const disconnect = async (platform) => {
    try {
      await api.post(`/integrations/${platform}/disconnect`);
      toast.success("Disconnected");
      load();
    } catch { toast.error("Failed"); }
  };

  const sendTestPost = async (platform) => {
    setTestPosting(platform);
    try {
      const { data } = await api.post(`/integrations/${platform}/test-post`);
      const target = data.page_name || (data.ig_username ? `@${data.ig_username}` : "your account");
      toast.success(`Test post published to ${target}!`, {
        duration: 10000,
        action: data.external_url
          ? { label: "View post", onClick: () => window.open(data.external_url, "_blank") }
          : undefined,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test post failed", { duration: 8000 });
    } finally { setTestPosting(null); }
  };

  const grouped = CATEGORY_ORDER.map((cat) => ({
    cat,
    items: items.filter((i) => i.category === cat),
  }));

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="integrations-page">
      <PageHeader
        eyebrow="// INTEGRATIONS"
        title="Connect your broadcast stack."
        description="Hook up social platforms, distribution networks, and live streams. Save credentials here — connections activate once provider keys are wired in."
      />

      <div className="mb-6 rounded-xl border border-brand-amber/40 bg-brand-amber/5 p-4 flex items-start gap-3" data-testid="integrations-notice">
        <AlertCircle className="h-5 w-5 text-brand-amber shrink-0 mt-0.5" />
        <div className="text-sm text-ink-secondary">
          <span className="text-ink-primary font-medium">Facebook &amp; Instagram (Meta)</span> connect instantly — paste your Page Access Token and we verify it live against the Meta Graph API (user tokens are auto-exchanged for Page tokens; the linked Instagram Business account is auto-discovered). Other social platforms (X, TikTok, YouTube…) require their own developer keys and move to <span className="text-brand-amber font-mono">PENDING_KEYS</span> until the OAuth client is wired in production. <span className="text-ink-primary font-medium">Self-serve integrations</span> — Myriad, Telegram, Discord, Bluesky, Apple Podcasts RSS, and streaming servers — connect instantly once you save valid credentials.
        </div>
      </div>

      <ConnectionHealthCard />

      {loading ? (
        <div className="text-center py-20 font-mono text-xs text-ink-muted">LOADING INTEGRATIONS...</div>
      ) : (
        <div className="space-y-10">
          {grouped.map(({ cat, items: catItems }) => (
            <div key={cat} data-testid={`category-${cat}`}>
              <h2 className="font-display text-2xl font-bold tracking-tighter mb-4">{CATEGORY_LABELS[cat]}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {catItems.map((it) => {
                  const isBR = it.platform === "broadcast_radio_uk";
                  return (
                  <div key={it.platform} className={`rounded-xl border bg-bg-surface p-5 lift ${isBR ? "border-cyan-500/40" : "border-line"}`} data-testid={`int-${it.platform}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {isBR ? <Radio className="h-4 w-4 text-cyan-300" /> : <Plug className="h-4 w-4 text-brand-amber" />}
                        <span className="font-display font-bold">{it.label}</span>
                      </div>
                      <StatusBadge status={it.status} />
                    </div>
                    <div className="text-xs text-ink-muted mb-2">Needs: {it.requires}</div>
                    {it.status === "error" && it.status_message && (
                      <div className="rounded-md border border-red-500/30 bg-red-500/5 px-2.5 py-1.5 mb-2 text-[11px] text-red-300 leading-relaxed"
                           data-testid={`int-error-${it.platform}`}>
                        {it.status_message}
                      </div>
                    )}
                    {it.description && (
                      <div className="text-[11px] text-ink-secondary leading-relaxed mb-3 italic">
                        {it.description}
                      </div>
                    )}
                    {it.requires_software && (
                      <div className="rounded-md border border-brand-amber/30 bg-brand-amber/5 px-2.5 py-1.5 mb-2"
                           data-testid={`int-requires-${it.platform}`}>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-brand-amber mb-0.5">
                          Requires
                        </div>
                        <div className="text-[11px] text-ink-primary">{it.requires_software}</div>
                      </div>
                    )}
                    {it.skip_if && (
                      <div className="rounded-md border border-line bg-bg-elevated/60 px-2.5 py-1.5 mb-3"
                           data-testid={`int-skip-${it.platform}`}>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-0.5">
                          Skip if
                        </div>
                        <div className="text-[11px] text-ink-secondary leading-snug">{it.skip_if}</div>
                      </div>
                    )}
                    {it.help && (
                      <details className="mb-3 rounded-md border border-line bg-bg-elevated/40 px-2.5 py-1.5"
                               data-testid={`int-help-${it.platform}`}>
                        <summary className="cursor-pointer text-[11px] font-mono uppercase tracking-wider text-cyan-300 hover:text-cyan-200 transition select-none">
                          {it.help.title || "What's the difference?"}
                        </summary>
                        <div className="mt-2">
                          {it.help.intro && (
                            <p className="text-[11px] text-ink-secondary leading-snug mb-2">
                              {it.help.intro}
                            </p>
                          )}
                          {Array.isArray(it.help.columns) && Array.isArray(it.help.rows) && (
                            <div className="overflow-x-auto">
                              <table className="w-full text-[11px]">
                                <thead className="text-left text-[10px] font-mono uppercase tracking-wider text-ink-muted">
                                  <tr className="border-b border-line/40">
                                    {it.help.columns.map((c, i) => (
                                      <th key={i} className="py-1 pr-2 align-top">{c}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {it.help.rows.map((row, ri) => (
                                    <tr key={ri} className="border-b border-line/20 last:border-0">
                                      {row.map((cell, ci) => (
                                        <td key={ci} className={`py-1 pr-2 align-top ${ci === 0 ? "text-ink-muted" : "text-ink-primary"}`}>
                                          {cell}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                          {it.help.footnote && (
                            <p className="mt-2 text-[11px] text-ink-secondary italic leading-snug">
                              {it.help.footnote}
                            </p>
                          )}
                        </div>
                      </details>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEdit(it)}
                        data-testid={`int-configure-${it.platform}`}
                        className="text-xs rounded-md border border-line bg-bg-elevated px-3 py-1.5 hover:text-cyan-300 transition"
                      >
                        {it.status === "not_connected" ? "Configure" : "Update"}
                      </button>
                      {["meta_facebook", "meta_instagram"].includes(it.platform) && it.status === "connected" && (
                        <button
                          onClick={() => sendTestPost(it.platform)}
                          disabled={!!testPosting}
                          data-testid={`int-testpost-${it.platform}`}
                          className="text-xs rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 px-3 py-1.5 hover:bg-emerald-500/20 transition disabled:opacity-50"
                        >
                          {testPosting === it.platform ? "Posting…" : "Send test post"}
                        </button>
                      )}
                      {it.status !== "not_connected" && (
                        <button
                          onClick={() => disconnect(it.platform)}
                          data-testid={`int-disconnect-${it.platform}`}
                          className="text-xs rounded-md border border-line bg-bg-elevated px-3 py-1.5 hover:text-brand-onair transition"
                        >
                          Disconnect
                        </button>
                      )}
                      {it.docs_url && (
                        <a
                          href={it.docs_url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`int-docs-${it.platform}`}
                          className="ml-auto text-[11px] inline-flex items-center gap-1 text-ink-muted hover:text-cyan-300 transition"
                        >
                          Docs <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-bg-surface border-line text-ink-primary" data-testid="int-config-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              {editing?.platform === "broadcast_radio_uk" && <Radio className="h-5 w-5 text-cyan-300" />}
              {editing?.label}
            </DialogTitle>
            <DialogDescription className="text-ink-secondary">
              {editing?.description || "Save the credentials for this platform. Values are stored encrypted-at-rest and never logged."}
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 py-2">
              {(CONFIG_FIELDS[editing.platform] || ["api_key"]).map((k) => (
                <div key={k}>
                  <Label className="text-xs uppercase tracking-wider text-ink-secondary">{k.replace(/_/g, " ")}</Label>
                  <Input
                    value={config[k] || ""}
                    onChange={(e) => setConfig({ ...config, [k]: e.target.value })}
                    className="mt-1 bg-bg border-line font-mono text-xs"
                    placeholder={k === "api_url" ? "https://yourstation.broadcastradio.com/xapi" : `Enter ${k}`}
                    data-testid={`config-field-${k}`}
                  />
                </div>
              ))}
              {CONFIG_HELP[editing.platform] && (
                <div className="text-[11px] text-ink-secondary font-mono pt-1 leading-relaxed">
                  {CONFIG_HELP[editing.platform].note}{" "}
                  <a className="text-cyan-300 inline-flex items-center gap-1" href={CONFIG_HELP[editing.platform].url} target="_blank" rel="noreferrer">
                    {CONFIG_HELP[editing.platform].label} <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                </div>
              )}
              {!CONFIG_HELP[editing.platform] && (
                <div className="text-[10px] text-ink-muted font-mono pt-1">
                  Need help finding these? See platform docs: <a className="text-brand-amber inline-flex items-center gap-1" href="https://developers.facebook.com/" target="_blank" rel="noreferrer">developers.facebook.com <ExternalLink className="h-2.5 w-2.5" /></a>
                </div>
              )}

              {testResult && (
                <div className={`rounded-lg border p-3 ${testResult.ok ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`} data-testid="int-test-result">
                  {testResult.ok ? (
                    <div className="flex items-start gap-2 text-xs">
                      <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-ink-primary">Connection healthy</div>
                        <div className="text-[11px] text-ink-muted mt-0.5">
                          {testResult.myriad_version ? `Myriad v${testResult.myriad_version}` : ""}
                          {testResult.station ? ` · ${testResult.station}` : ""}
                          {testResult.uptime ? ` · ${Math.round(testResult.uptime / 3600)}h uptime` : ""}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2 text-xs">
                      <XIcon className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold text-ink-primary">Connection failed</div>
                        <div className="text-[11px] text-ink-muted mt-0.5 whitespace-pre-wrap break-words">{testResult.error}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            {editing?.platform === "broadcast_radio_uk" && (
              <button
                onClick={testBroadcastRadio}
                disabled={testing || saving}
                data-testid="int-test-btn"
                className="inline-flex items-center gap-1.5 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-50"
              >
                {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Test connection
              </button>
            )}
            <button onClick={save} disabled={saving} data-testid="int-save-btn" className="rounded-md bg-brand-amber px-5 py-2 font-semibold text-black amber-glow disabled:opacity-50">
              {saving ? "Saving..." : "Save configuration"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    not_connected: { label: "NOT CONNECTED", cls: "border-line bg-bg-elevated text-ink-muted" },
    pending_keys: { label: "PENDING KEYS", cls: "border-brand-amber/40 bg-brand-amber/10 text-brand-amber" },
    connected: { label: "CONNECTED", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
    error: { label: "VERIFY FAILED", cls: "border-red-500/40 bg-red-500/10 text-red-300" },
  };
  const v = map[status] || map.not_connected;
  return <span className={`rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${v.cls}`}>{v.label}</span>;
}
